import { useTranslations } from 'next-intl';
import { type FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';

import { AppStoreIcon } from '@/app/[locale]/(not-authorized)/account/login/components/Login/components/TwoFaSetup/components/Step0/components/AppStoreIcon';
import { GooglePlayIcon } from '@/app/[locale]/(not-authorized)/account/login/components/Login/components/TwoFaSetup/components/Step0/components/GooglePlayIcon';
import { Button } from '@/components/common/button/Button';
import { ButtonLink } from '@/components/common/button/ButtonLink';
import { Flex } from '@/components/common/Flex';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { Text } from '@/components/common/Text';
import { richComponents } from '@/constants/rich/richComponents';
import { useCommonApolloOptions } from '@/hooks/use-common-apollo-options';

import { gql } from '../../../../../../../../../../../../generated';

import styles from './Step0.module.scss';

type Step0Props = {
  onSubmit: (qrCodeData: string) => unknown;
  onSkip: () => unknown;
};

const RequestQrCode = gql(/* GraphQL */ `
  mutation RequestQrCode {
    otpRequestActivate {
      qr_code_data
    }
  }
`);

export const Step0: FC<Step0Props> = (props) => {
  const t = useTranslations('pages.account.login.form.setup2FaBlock.steps.step0');

  const { onSubmit, onSkip } = props;

  const form = useForm({
    mode: 'onBlur',
  });

  const { handleSubmit } = form;

  const apolloOptions = useCommonApolloOptions();

  const [requestQr] = useMutation(RequestQrCode, apolloOptions);

  const generateQrCodeData = useCallback(async () => {
    const { data } = await requestQr();

    if (!data) {
      return;
    }

    onSubmit(data.otpRequestActivate.qr_code_data);
  }, [onSubmit, requestQr]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(generateQrCodeData)}>
        <Flex direction="column" gap={9}>
          <Flex className={styles.titleBlock} direction="column" gap={4}>
            <Text type="medium64" whiteSpace="preWrap">
              {t('title')}
            </Text>

            <Text type="regular18" color="white-50p">
              {t('subtitle')}
            </Text>
          </Flex>

          <Flex className={styles.formContainer} direction="column">
            <Text type="regular16" color="white-32p">
              {t.rich('description', richComponents)}
            </Text>

            <Flex className={styles.downloadBlock} gap={4} alignItems="center" justifyContent="spaceBetween">
              <Text type="medium18">{t('downloadBlock.title')}</Text>

              <Flex gap={4}>
                <ButtonLink
                  variant="largeIcon"
                  href="https://apps.apple.com/ru/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <AppStoreIcon />
                </ButtonLink>

                <ButtonLink
                  variant="largeIcon"
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <GooglePlayIcon />
                </ButtonLink>
              </Flex>
            </Flex>

            <Flex direction="column" gap={4}>
              <FormSubmitButton>{t('buttons.enable')}</FormSubmitButton>

              <Button variant="secondary" onClick={onSkip}>
                {t('buttons.skip')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
